import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { warehousesApi } from '../../services/api';
import { WarehouseFormData } from '../../types/warehouse';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Divider,
  SelectChangeEvent
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';

const WarehouseForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState<WarehouseFormData>({
    name: '',
    location: '',
    capacity: undefined,
    manager: '',
    phone: '',
    description: '',
    status: 'active'
  });
  
  const [loading, setLoading] = useState<boolean>(isEditMode);
  const [saving, setSaving] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchWarehouse = async () => {
      if (isEditMode && id) {
        try {
          setLoading(true);
          const data = await warehousesApi.getWarehouse(id);
          setFormData({
            name: data.name,
            location: data.location || '',
            capacity: data.capacity,
            manager: data.manager || '',
            phone: data.phone || '',
            description: data.description || '',
            status: data.status
          });
          setLoading(false);
        } catch (err) {
          console.error('Error fetching warehouse', err);
          setLoading(false);
        }
      }
    };

    fetchWarehouse();
  }, [id, isEditMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!name) return;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (!name) return;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'اسم المستودع مطلوب';
    }
    
    if (formData.capacity !== undefined && (isNaN(Number(formData.capacity)) || Number(formData.capacity) < 0)) {
      newErrors.capacity = 'الرجاء إدخال قيمة صالحة للسعة';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSaving(true);
      
      if (isEditMode && id) {
        await warehousesApi.updateWarehouse(id, formData);
      } else {
        await warehousesApi.createWarehouse(formData);
      }
      
      setSaving(false);
      navigate('/warehouses/list');
    } catch (err) {
      console.error('Error saving warehouse', err);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/warehouses/list');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto', mt: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          رجوع
        </Button>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1, textAlign: 'right' }}>
          {isEditMode ? 'تعديل المستودع' : 'إضافة مستودع جديد'}
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 4 }} />
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3} dir="rtl">
          <Grid item xs={12} md={6}>
            <TextField
              name="name"
              label="اسم المستودع"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
              error={Boolean(errors.name)}
              helperText={errors.name}
              dir="rtl"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="location"
              label="الموقع"
              value={formData.location}
              onChange={handleInputChange}
              fullWidth
              dir="rtl"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="capacity"
              label="السعة"
              type="number"
              value={formData.capacity || ''}
              onChange={handleInputChange}
              fullWidth
              error={Boolean(errors.capacity)}
              helperText={errors.capacity}
              dir="rtl"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="status-label">الحالة</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                value={formData.status}
                onChange={handleSelectChange}
                label="الحالة"
              >
                <MenuItem value="active">نشط</MenuItem>
                <MenuItem value="inactive">غير نشط</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="manager"
              label="المدير المسؤول"
              value={formData.manager}
              onChange={handleInputChange}
              fullWidth
              dir="rtl"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="phone"
              label="رقم الهاتف"
              value={formData.phone}
              onChange={handleInputChange}
              fullWidth
              dir="rtl"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="description"
              label="وصف المستودع"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={4}
              dir="rtl"
            />
          </Grid>
          
          <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              type="button"
              variant="outlined"
              onClick={handleCancel}
              sx={{ ml: 2 }}
            >
              إلغاء
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
              onClick={handleSubmit}
            >
              {saving ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  جاري الحفظ...
                </>
              ) : (
                isEditMode ? 'حفظ التغييرات' : 'إضافة المستودع'
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default WarehouseForm;